import Button from 'tio-ui/components/button';
import { dropTask } from 'ember-concurrency';
import { not, and } from 'tio-ui/utilities';
import * as v from 'valibot';
import { PhCheck, PhPencil, PhX, PhPlus } from 'ember-phosphor-icons';
import { Section } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import Form from 'tio-ui/components/form';
import Input from 'tio-ui/components/input';
import type { FormResult } from 'tio-ui/components/form';
import type { EmailAddressExtension as EmailAddressModel } from 'tio-data/data/resources/email-address.ext';
import type { MatchParticipantExtension as MatchParticipantModel } from 'tio-data/data/resources/match-participant.ext';
import type Owner from '@ember/owner';
import type PartnerService from 'tio-employee/services/partner';
import type SessionContextService from 'tio-employee/services/session-context';
import type { Store as StoreService } from '@warp-drive/core';
import type { IntlService } from 'ember-intl';
import type { NotificationsService } from 'tio-ui/components/notifications';
import { saveRecord } from 'tio-data/requests/save-record';

interface EmailFormData {
  email: string;
}

interface SyfDashboardPersonalEmailSignature {
  Args: {
    matchParticipant: MatchParticipantModel;
  };
}

class SyfDashboardPersonalEmailComponent extends Component<SyfDashboardPersonalEmailSignature> {
  @service declare partner: PartnerService;
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;

  @tracked emailAddress?: EmailAddressModel;
  @tracked isEditing: boolean = false;
  @tracked originalEmail: string;
  @tracked formData: EmailFormData;

  constructor(owner: Owner, args: SyfDashboardPersonalEmailSignature['Args']) {
    super(owner, args);
    this.originalEmail = this.args.matchParticipant?.emailAddress?.email || '';
    this.formData = { email: this.originalEmail };
  }

  get #schema() {
    return v.object({
      email: v.pipe(
        v.string(),
        v.trim(),
        v.nonEmpty(this.intl.t('syf.email_validation.required')),
        v.email(this.intl.t('syf.email_validation.invalid'))
      ),
    });
  }

  get disableSaveEmail() {
    const valueChanged = this.originalEmail !== this.formData.email.trim();
    return !valueChanged || !v.safeParse(this.#schema, this.formData).success;
  }

  validate = (data: EmailFormData) => {
    const r = v.safeParse(this.#schema, data);
    return r.success ? {} : (v.flatten(r.issues).nested ?? {});
  };

  handleFormChange = (result: FormResult<EmailFormData>) => {
    this.formData = result.data;
  };

  saveEmail = dropTask(async () => {
    const participant = this.args.matchParticipant;
    const emailAddress = this.#editableEmailAddress();
    const email = this.formData.email.trim();

    // A saved address whose link failed is retried without being sent again.
    if (emailAddress.isNew || emailAddress.email !== email) {
      emailAddress.email = email;
      try {
        await saveRecord(this.store, emailAddress);
      } catch (error) {
        if (emailAddress.isNew) {
          emailAddress.unloadRecord();
          this.emailAddress = undefined;
        } else {
          emailAddress.rollbackAttributes();
        }
        this.#reportFailure(error);
        return;
      }
    }

    if (participant.emailAddress !== emailAddress) {
      const linked = participant.emailAddress;
      participant.emailAddress = emailAddress;
      try {
        await saveRecord(this.store, participant);
      } catch (error) {
        participant.emailAddress = linked;
        this.#reportFailure(error);
        return;
      }
    }

    this.originalEmail = emailAddress.email;
    this.formData = { email: emailAddress.email };
    this.isEditing = false;
  });

  handleSubmit = () => {
    this.saveEmail.perform();
  };

  toggleEdit = () => {
    this.isEditing = !this.isEditing;
  };

  cancel = () => {
    this.isEditing = false;
    this.formData = { email: this.originalEmail };
  };

  #editableEmailAddress() {
    this.emailAddress ??=
      this.args.matchParticipant.emailAddress ??
      this.sessionContext.user.personalEmail ??
      this.store.createRecord<EmailAddressModel>('email-address', {
        contactType: 'PERSONAL',
        user: this.sessionContext.user,
        email: '',
        isActive: true,
      });
    return this.emailAddress;
  }

  #reportFailure(error: unknown) {
    console.error(error);
    this.notifications.add(this.intl.t('syf.dashboard.email.save_error'), {
      appearance: 'error',
    });
  }

  <template>
    <Section data-test-personal-email-section>
      <:header>
        {{t "syf.dashboard.email.header"}}
      </:header>
      <:body>
        <div class="text-sm mb-4">
          {{t "syf.dashboard.email.content"}}
        </div>
        <div class="tio-flex-center lg:justify-start my-2 mx-4" data-test-personal-email-container>
          {{#if (and (not this.formData.email) (not this.isEditing))}}
            <Button
              @appearance="outlined"
              @intent="default"
              @icon={{PhPlus}}
              @iconPlacement="start"
              {{on "click" this.toggleEdit}}
              class="uppercase tio-text-sm-semibold"
              data-test-add-email-button
            >
              {{t "syf.dashboard.email.add_email"}}
            </Button>
          {{else}}
            <Form
              @data={{this.formData}}
              @validate={{this.validate}}
              @onChange={{this.handleFormChange}}
              @onSubmit={{this.handleSubmit}}
              as |form|
            >
              <div class="tio-row-top-tight">
                {{#let (form.field "email") as |f|}}
                  <Input
                    @value={{f.value}}
                    @onChange={{f.onChange}}
                    @onBlur={{f.onBlur}}
                    @errors={{f.errors}}
                    title={{this.formData.email}}
                    @isDisabled={{not this.isEditing}}
                    class="text-center"
                    data-test-email-field
                  />
                {{/let}}

                {{#if this.isEditing}}
                  <div class="flex">
                    <Button
                      @appearance="outlined"
                      {{on "click" this.cancel}}
                      @isDisabled={{this.saveEmail.isRunning}}
                      title={{t "common.cancel"}}
                      data-test-cancel-button
                    >
                      <PhX @size="20" />
                    </Button>
                    <Button
                      type="submit"
                      @isDisabled={{this.disableSaveEmail}}
                      title={{t "common.save"}}
                      data-test-save-button
                    >
                      <PhCheck @size="20" />
                    </Button>
                  </div>
                {{else}}
                  <Button
                    @appearance="outlined"
                    @intent="primary"
                    {{on "click" this.toggleEdit}}
                    title={{t "common.edit"}}
                    class="mx-1"
                    data-test-edit-button
                  >
                    <PhPencil @size="20" />
                  </Button>
                {{/if}}
              </div>
            </Form>
          {{/if}}
        </div>
      </:body>
    </Section>
  </template>
}

export default SyfDashboardPersonalEmailComponent;
